import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useLanguage } from '@/lib/i18n';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Search, Plus, Edit, Trash2, Clock, IndianRupee } from 'lucide-react';

const categoryColors = {
  Hair: 'bg-blue-100 text-blue-700', Skin: 'bg-pink-100 text-pink-700',
  Nails: 'bg-purple-100 text-purple-700', Spa: 'bg-green-100 text-green-700',
  General: 'bg-gray-100 text-gray-700'
};

const emptyForm = { name: '', category: 'Hair', duration_mins: 30, price: '', gst_rate: 18, active: true };

export default function Services() {
  const [services, setServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const { t } = useLanguage();

  useEffect(() => { loadServices(); }, []);

  const loadServices = async () => {
    try {
      const r = await api.get('/services');
      setServices(r.data);
    } catch { toast.error(t('svc_load_error')); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name || !form.price) { toast.error(t('svc_req')); return; }
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price), duration_mins: Number(form.duration_mins), gst_rate: Number(form.gst_rate) };
      if (editingId) {
        await api.put(`/services/${editingId}`, payload);
        toast.success(t('svc_updated'));
      } else {
        await api.post('/services', payload);
        toast.success(t('svc_added'));
      }
      setDialogOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      loadServices();
    } catch { toast.error(t('svc_save_error')); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/services/${deleteId}`);
      toast.success(t('svc_deleted'));
      setDeleteId(null);
      loadServices();
    } catch { toast.error(t('svc_delete_error')); }
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({ name: s.name, category: s.category, duration_mins: s.duration_mins, price: s.price, gst_rate: s.gst_rate, active: s.active });
    setDialogOpen(true);
  };

  const categories = ['All', ...new Set(services.map(s => s.category))];
  const filtered = services.filter(s => {
    const matchCat = category === 'All' || s.category === category;
    const matchSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCat && matchSearch;
  });

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input data-testid="service-search" placeholder={t('svc_search')} className="h-14 pl-12 text-lg rounded-xl" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <Button
          data-testid="add-service-btn"
          onClick={() => { setEditingId(null); setForm(emptyForm); setDialogOpen(true); }}
          className="h-14 px-6 rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] text-lg font-semibold gap-2"
        >
          <Plus className="w-5 h-5" /> {t('svc_add')}
        </Button>
      </div>

      {/* Category Filter */}
      <Tabs value={category} onValueChange={setCategory}>
        <TabsList className="bg-white rounded-xl p-1 h-auto flex flex-wrap gap-1">
          {categories.map(cat => (
            <TabsTrigger key={cat} value={cat} data-testid={`svc-cat-${cat.toLowerCase()}`} className="rounded-lg px-4 py-2.5 text-base data-[state=active]:bg-[#1a1a2e] data-[state=active]:text-white">
              {cat}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Service Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.length === 0 ? (
          <div className="col-span-full text-center text-gray-400 py-12 text-lg">{t('svc_none')}</div>
        ) : filtered.map(s => (
          <Card key={s.id} className={`border-0 shadow-sm rounded-2xl hover:shadow-md transition-all ${!s.active ? 'opacity-50' : ''}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <Badge className={categoryColors[s.category] || categoryColors.General}>{s.category}</Badge>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="w-8 h-8 rounded-lg" data-testid={`edit-svc-${s.id}`} onClick={() => openEdit(s)}>
                    <Edit className="w-4 h-4 text-gray-400" />
                  </Button>
                  <Button variant="ghost" size="icon" className="w-8 h-8 rounded-lg" data-testid={`delete-svc-${s.id}`} onClick={() => setDeleteId(s.id)}>
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </Button>
                </div>
              </div>
              <h3 className="font-semibold text-lg text-[#1a1a2e] mb-2">{s.name}</h3>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1 text-[#f0a500] font-bold text-xl">
                  <IndianRupee className="w-4 h-4" />{s.price}
                </span>
                <span className="flex items-center gap-1 text-gray-400 text-sm">
                  <Clock className="w-3.5 h-3.5" />{s.duration_mins}m
                </span>
              </div>
              {!s.active && <Badge variant="secondary" className="mt-2">{t('svc_inactive')}</Badge>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#faf8f5] rounded-2xl sm:rounded-3xl max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-poppins text-lg sm:text-xl">{editingId ? t('svc_edit') : t('svc_add')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 sm:mt-4">
            <div>
              <Label className="text-base mb-1.5 block">{t('svc_name')} *</Label>
              <Input data-testid="svc-form-name" className="h-11 sm:h-12 rounded-xl" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-base mb-1.5 block">{t('svc_category')}</Label>
                <select
                  data-testid="svc-form-category"
                  className="w-full h-11 sm:h-12 rounded-xl border border-gray-200 px-3 text-base bg-white"
                  value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                >
                  {['Hair', 'Skin', 'Nails', 'Spa', 'General'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-base mb-1.5 block">{t('svc_duration')}</Label>
                <Input data-testid="svc-form-duration" type="number" className="h-11 sm:h-12 rounded-xl" value={form.duration_mins} onChange={e => setForm(f => ({ ...f, duration_mins: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-base mb-1.5 block">{t('svc_price')} *</Label>
                <Input data-testid="svc-form-price" type="number" className="h-11 sm:h-12 rounded-xl" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
              </div>
              <div>
                <Label className="text-base mb-1.5 block">{t('svc_gst')}</Label>
                <select
                  data-testid="svc-form-gst"
                  className="w-full h-11 sm:h-12 rounded-xl border border-gray-200 px-3 text-base bg-white"
                  value={form.gst_rate}
                  onChange={e => setForm(f => ({ ...f, gst_rate: Number(e.target.value) }))}
                >
                  {[0, 5, 12, 18].map(r => <option key={r} value={r}>{r}%</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="svc-form-active" checked={form.active} onCheckedChange={v => setForm(f => ({ ...f, active: v }))} />
              <Label className="text-base">{t('svc_active')}</Label>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end pt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="h-11 sm:h-12 px-6 rounded-xl text-base w-full sm:w-auto">{t('cancel')}</Button>
            <Button data-testid="svc-form-save-btn" onClick={handleSave} disabled={saving} className="h-11 sm:h-12 px-8 rounded-xl bg-[#1a1a2e] hover:bg-[#2d2d44] text-white text-base w-full sm:w-auto">
              {saving ? t('saving') : t('save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="rounded-2xl max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-poppins">{t('del_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('del_confirm_desc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t('del_cancel')}</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-svc-btn" onClick={handleDelete} className="bg-red-500 hover:bg-red-600 rounded-xl">{t('del_confirm_btn')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
